import { API_ENDPOINTS } from '../config/api'
import { safeJson } from '../lib/utils'

type ApiResult<T> = { success: boolean; data?: T; message?: string }

async function apiFetch<T>(url: string, token: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init?.headers || {}),
    },
  })
  const body = (await safeJson(res)) as ApiResult<T> & { message?: string }
  if (!res.ok) throw new Error(body?.message || `Request failed (${res.status})`)
  if (body && typeof body === 'object' && 'success' in body && body.success === false) {
    throw new Error(body.message || 'Request failed')
  }
  return (body?.data ?? body) as T
}

function orgQuery(orgId?: string | null, extra?: Record<string, string | undefined>) {
  const p = new URLSearchParams()
  if (orgId) p.set('organization_id', orgId)
  if (extra) {
    for (const [k, v] of Object.entries(extra)) {
      if (v != null && v !== '') p.set(k, v)
    }
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

export type GraphNode = {
  id: string
  type: string
  label: string
  status?: string | null
  metadata?: Record<string, unknown>
}

export type GraphEdge = {
  id: string
  source: string
  target: string
  type: string
  label?: string | null
  metadata?: Record<string, unknown>
}

export type GraphData = {
  nodes: GraphNode[]
  edges: GraphEdge[]
  summary: { nodeCount: number; edgeCount: number; missingAreas: string[] }
}

export type CompletenessChecklist = {
  items: Array<{ id: string; complete: boolean; message: string; optional?: boolean }>
  completeCount: number
  totalCount: number
  missingItems: string[]
}

export type DataFlowRow = {
  id: string
  sourceSystemId: string
  targetSystemId: string
  dataCategory: string
  flowType: string
  direction: string
  description?: string | null
  protectionMechanism?: string | null
  crossBorderTransfer: boolean
  thirdPartyInvolved: boolean
  sourceSystem?: { id: string; name: string; code?: string | null } | null
  targetSystem?: { id: string; name: string; code?: string | null } | null
}

export type ControlCatalogEntry = {
  canonicalCode: string
  controlId: string
  controlTitle: string
  standardCode: string | null
  severity?: string | null
}

export async function fetchSystemGraph(
  token: string,
  systemId: string,
  orgId?: string | null,
  options?: {
    layers?: string
    failedControlsOnly?: boolean
    internetExposed?: boolean
    pciOnly?: boolean
    evidenceGaps?: boolean
    remediationBlockers?: boolean
    criticalPaths?: boolean
    overlayReadiness?: boolean
    slaBreach?: boolean
    staleEvidence?: boolean
    remediationAging?: boolean
    blastRadius?: boolean
    readinessBlockers?: boolean
  }
) {
  return apiFetch<GraphData>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_GRAPH(systemId)}${orgQuery(orgId, {
      layers: options?.layers,
      failedControlsOnly: options?.failedControlsOnly ? 'true' : undefined,
      internetExposed: options?.internetExposed ? 'true' : undefined,
      pciOnly: options?.pciOnly ? 'true' : undefined,
      evidenceGaps: options?.evidenceGaps ? 'true' : undefined,
      remediationBlockers: options?.remediationBlockers ? 'true' : undefined,
      criticalPaths: options?.criticalPaths ? 'true' : undefined,
      overlayReadiness: options?.overlayReadiness ? 'true' : undefined,
      slaBreach: options?.slaBreach ? 'true' : undefined,
      staleEvidence: options?.staleEvidence ? 'true' : undefined,
      remediationAging: options?.remediationAging ? 'true' : undefined,
      blastRadius: options?.blastRadius ? 'true' : undefined,
      readinessBlockers: options?.readinessBlockers ? 'true' : undefined,
    })}`,
    token
  )
}

export async function fetchCompleteness(token: string, systemId: string, orgId?: string | null) {
  return apiFetch<CompletenessChecklist>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_COMPLETENESS(systemId)}${orgQuery(orgId)}`,
    token
  )
}

export async function searchControlCatalog(
  token: string,
  params: { standardCode?: string; query?: string; limit?: number }
) {
  return apiFetch<{
    catalogAvailable: boolean
    manualEntryAllowed: boolean
    controls: ControlCatalogEntry[]
  }>(
    `${API_ENDPOINTS.ASSETS_CONTROL_CATALOG}${orgQuery(null, {
      standardCode: params.standardCode,
      query: params.query,
      limit: params.limit != null ? String(params.limit) : undefined,
    })}`,
    token
  )
}

export async function fetchDataFlows(token: string, orgId?: string | null, systemId?: string) {
  return apiFetch<DataFlowRow[]>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_DATA_FLOWS}${orgQuery(orgId, { systemId })}`,
    token
  )
}

export async function createDataFlow(token: string, payload: Record<string, unknown>, orgId?: string | null) {
  return apiFetch<DataFlowRow>(`${API_ENDPOINTS.INFORMATION_SYSTEM_DATA_FLOWS}${orgQuery(orgId)}`, token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

export async function deleteDataFlow(token: string, flowId: string, orgId?: string | null) {
  return apiFetch<{ deleted: boolean }>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_DATA_FLOW(flowId)}${orgQuery(orgId)}`,
    token,
    { method: 'DELETE' }
  )
}

export function downloadCsvExport(token: string, url: string) {
  return fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then(async (res) => {
    if (!res.ok) throw new Error('Export failed')
    return res.text()
  })
}
